import { useRef, useEffect, useCallback, useState } from 'react';

interface Bar {
  x: number;
  baseHeight: number;
  currentHeight: number;
  phase: number;
  speed: number;
  glowIntensity: number;
  glowColor: [number, number, number];
}

const GLOW_COLORS: [number, number, number][] = [
  [212, 165, 116],  // warm gold
  [255, 130, 80],   // coral orange
  [100, 210, 255],  // electric blue
  [80, 255, 180],   // neon mint
  [255, 100, 180],  // hot pink
  [180, 120, 255],  // violet
  [255, 220, 80],   // yellow gold
  [80, 255, 255],   // cyan
];

export default function InteractiveWaveform() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const barsRef = useRef<Bar[]>([]);
  const mouseRef = useRef<{ x: number; y: number } | null>(null);
  const animFrameRef = useRef<number>(0);
  const lastMouseXRef = useRef<number>(-1);
  const [isReady, setIsReady] = useState(false);
  const isVisibleRef = useRef(true);
  const mouseActiveRef = useRef(false);
  const mouseTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

  const initBars = useCallback((width: number) => {
    const barWidth = 3;
    const gap = 6;
    const count = Math.floor(width / (barWidth + gap));
    barsRef.current = Array.from({ length: count }, (_, i) => {
      const colorIndex = Math.floor(Math.random() * GLOW_COLORS.length);
      return {
        x: i * (barWidth + gap) + barWidth / 2,
        baseHeight: 0.06 + Math.abs(Math.sin(i * 0.15 + Math.random() * 0.8)) * 0.5 + Math.random() * 0.12,
        currentHeight: 0,
        phase: Math.random() * Math.PI * 2,
        speed: 0.3 + Math.random() * 0.7,
        glowIntensity: 0,
        glowColor: GLOW_COLORS[colorIndex],
      };
    });
  }, []);

  const draw = useCallback((time: number) => {
    const canvas = canvasRef.current;
    if (!canvas || !isVisibleRef.current) {
      animFrameRef.current = requestAnimationFrame(draw);
      return;
    }
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const W = canvas.width;
    const H = canvas.height;
    const barWidth = 3;
    const mouse = mouseRef.current;

    ctx.clearRect(0, 0, W, H);

    // Draw ambient glow under active zone
    if (mouse && mouseActiveRef.current) {
      const ambientGrad = ctx.createRadialGradient(mouse.x, H / 2, 0, mouse.x, H / 2, 120);
      ambientGrad.addColorStop(0, 'rgba(212,165,116,0.06)');
      ambientGrad.addColorStop(1, 'rgba(0,0,0,0)');
      ctx.fillStyle = ambientGrad;
      ctx.fillRect(0, 0, W, H);
    }

    barsRef.current.forEach((bar) => {
      // Animate base oscillation
      const osc = Math.sin(time * 0.001 * bar.speed + bar.phase) * 0.1;
      const targetHeight = bar.baseHeight + osc;

      // Mouse proximity glow
      let glow = 0;
      if (mouse && mouseActiveRef.current) {
        const dist = Math.abs(bar.x - mouse.x);
        const maxDist = 200;
        if (dist < maxDist) {
          const norm = 1 - dist / maxDist;
          glow = Math.pow(norm, 1.5);
        }
      }

      // Smooth glow transition — slower fade for lingering neon effect
      bar.glowIntensity += (glow - bar.glowIntensity) * 0.035;

      // Height boost on glow
      const heightBoost = bar.glowIntensity * 0.6;
      bar.currentHeight += (targetHeight + heightBoost - bar.currentHeight) * 0.1;

      const barH = bar.currentHeight * H;
      const x = bar.x - barWidth / 2;
      const y = H / 2 - barH / 2;

      const [r, g, b] = bar.glowColor;
      const baseAlpha = 0.12;
      const glowAlpha = 0.3 + bar.glowIntensity * 0.55;

      // Single glow layer (merged mid + bloom)
      if (bar.glowIntensity > 0.08) {
        ctx.save();
        ctx.shadowColor = `rgba(${r},${g},${b},${bar.glowIntensity * 0.5})`;
        ctx.shadowBlur = 6 + bar.glowIntensity * 10;
        const glowGrad = ctx.createLinearGradient(x, y, x, y + barH);
        glowGrad.addColorStop(0, `rgba(${r},${g},${b},0)`);
        glowGrad.addColorStop(0.5, `rgba(${r},${g},${b},${bar.glowIntensity * 0.5})`);
        glowGrad.addColorStop(1, `rgba(${r},${g},${b},0)`);
        ctx.fillStyle = glowGrad;
        ctx.beginPath();
        ctx.roundRect(x - 2, y - 3, barWidth + 4, barH + 6, barWidth / 2);
        ctx.fill();
        ctx.restore();
      }

      // Main bar
      const barGrad = ctx.createLinearGradient(x, y, x, y + barH);
      if (bar.glowIntensity > 0.08) {
        barGrad.addColorStop(0, `rgba(${r},${g},${b},${glowAlpha * 0.3})`);
        barGrad.addColorStop(0.3, `rgba(${r},${g},${b},${glowAlpha * 0.85})`);
        barGrad.addColorStop(0.5, `rgba(${r},${g},${b},${glowAlpha})`);
        barGrad.addColorStop(0.7, `rgba(${r},${g},${b},${glowAlpha * 0.85})`);
        barGrad.addColorStop(1, `rgba(${r},${g},${b},${glowAlpha * 0.3})`);
      } else {
        barGrad.addColorStop(0, `rgba(255,255,255,0)`);
        barGrad.addColorStop(0.5, `rgba(255,255,255,${baseAlpha})`);
        barGrad.addColorStop(1, `rgba(255,255,255,0)`);
      }
      ctx.fillStyle = barGrad;
      ctx.beginPath();
      ctx.roundRect(x, y, barWidth, barH, barWidth / 2);
      ctx.fill();

      // Bright white core line
      if (bar.glowIntensity > 0.25) {
        const coreAlpha = (bar.glowIntensity - 0.25) / 0.75;
        ctx.save();
        ctx.shadowColor = `rgba(255,255,255,${coreAlpha * 0.6})`;
        ctx.shadowBlur = 4;
        const lineGrad = ctx.createLinearGradient(bar.x, y, bar.x, y + barH);
        lineGrad.addColorStop(0, `rgba(255,255,255,0)`);
        lineGrad.addColorStop(0.3, `rgba(255,255,255,${coreAlpha * 0.8})`);
        lineGrad.addColorStop(0.5, `rgba(255,255,255,1)`);
        lineGrad.addColorStop(0.7, `rgba(255,255,255,${coreAlpha * 0.8})`);
        lineGrad.addColorStop(1, `rgba(255,255,255,0)`);
        ctx.fillStyle = lineGrad;
        ctx.beginPath();
        ctx.roundRect(bar.x - 0.75, y, 1.5, barH, 0.75);
        ctx.fill();
        ctx.restore();
      }
    });

    animFrameRef.current = requestAnimationFrame(draw);
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const resize = () => {
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
      initBars(canvas.width);
    };

    resize();
    setIsReady(true);

    const ro = new ResizeObserver(resize);
    ro.observe(canvas);

    animFrameRef.current = requestAnimationFrame(draw);

    return () => {
      ro.disconnect();
      cancelAnimationFrame(animFrameRef.current);
    };
  }, [initBars, draw]);

  // Visibility check - pause when off screen
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        isVisibleRef.current = entry.isIntersecting;
      },
      { threshold: 0 }
    );
    observer.observe(canvas);

    return () => observer.disconnect();
  }, []);

  // Throttled mouse tracking
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    let rafId: number;
    let pendingMouse: { x: number; y: number } | null = null;

    const handleMouseMove = (e: MouseEvent) => {
      const rect = canvas.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;

      if (x >= 0 && x <= rect.width && y >= 0 && y <= rect.height) {
        pendingMouse = { x, y };
        mouseActiveRef.current = true;

        clearTimeout(mouseTimeoutRef.current);
        mouseTimeoutRef.current = setTimeout(() => {
          mouseActiveRef.current = false;
        }, 1150);

        if (!rafId) {
          rafId = requestAnimationFrame(() => {
            if (pendingMouse) {
              mouseRef.current = pendingMouse;
              if (Math.abs(pendingMouse.x - lastMouseXRef.current) > 50) {
                lastMouseXRef.current = pendingMouse.x;
              }
            }
            rafId = 0;
          });
        }
      } else {
        pendingMouse = null;
        mouseRef.current = null;
      }
    };

    const handleMouseLeave = () => {
      mouseRef.current = null;
      mouseActiveRef.current = false;
    };

    window.addEventListener('mousemove', handleMouseMove, { passive: true });
    window.addEventListener('mouseleave', handleMouseLeave);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseleave', handleMouseLeave);
      clearTimeout(mouseTimeoutRef.current);
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className={`absolute inset-0 w-full h-full transition-opacity duration-700 ${isReady ? 'opacity-100' : 'opacity-0'}`}
      style={{ pointerEvents: 'none' }}
    />
  );
}